import { useMemo } from 'react'

import type { api } from './api'
import { useSession } from './SessionContext'

/* ============================================================================
   บัญชีผู้ดูแลระบบ (หน้า /admin/admins)
   backend: src/routes/adminUsers.js · ตาราง: sql/mssql/004_hr_login_new.sql

   ⛔ ทุกเส้นเป็นของ **superadmin เท่านั้น** รวมทั้งการอ่าน — role อื่นได้ 403
   ⚠️ เวลาทุกตัวเป็น **UTC** → แสดงด้วย formatUtcAsThai() เท่านั้น
   ============================================================================ */

export const USERS_BASE = '/api/admin/users'

export type Paged<T> = { total: number; page: number; pageSize: number } & T
export type PageOpts = { page?: number; pageSize?: number; sort?: string }

export type AdminRole = 'superadmin' | 'admin' | 'hr' | 'viewer'

export type AdminUser = {
  id: number
  username: string
  displayName: string | null
  email: string | null
  empCode: string | null
  role: AdminRole
  roleLabel: string
  status: 'active' | 'disabled'
  /** ตั้งรหัสชั่วคราวไว้ ยังไม่ได้เปลี่ยน — เข้าระบบแล้วจะถูกบังคับให้เปลี่ยนก่อนใช้เมนูอื่น */
  mustChangePassword: boolean
  /** ล็อกอยู่ "ตอนนี้" — backend คิดด้วยนาฬิกาของ DB ห้ามคำนวณเองจาก lockedUntil */
  locked: boolean
  lockedUntil: string | null
  failedCount: number
  lastLoginAt: string | null
  passwordUpdatedAt: string | null
  createdAt: string | null
  createdBy: string | null
  updatedAt: string | null
  updatedBy: string | null
  disabledAt: string | null
  disableReason: string | null
}

export type RoleOption = { value: AdminRole; label: string }

export type CreatePayload = {
  username: string
  displayName?: string
  email?: string
  empCode?: string
  role: AdminRole
  /** เว้นไว้ = ให้ระบบสุ่มให้ (ที่แนะนำ) */
  password?: string
}

export type UpdatePayload = Partial<Pick<CreatePayload, 'displayName' | 'email' | 'empCode' | 'role'>>

/** ผลลัพธ์ของการสร้าง/ตั้งรหัสใหม่ — `tempPassword` ดูได้ครั้งเดียว ไม่มีทางขอซ้ำ */
export type WithTempPassword = { user: AdminUser; tempPassword: string }

const query = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, v)
  const s = q.toString()
  return s ? `?${s}` : ''
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })
const num = (n: number | undefined) => (n === undefined ? undefined : String(n))

export function adminsApi(call: typeof api) {
  return {
    roles: () => call<{ roles: RoleOption[]; minPassword: number }>(`${USERS_BASE}/roles`),

    list: (opts: { status: 'active' | 'disabled' | 'all'; q?: string } & PageOpts) =>
      call<Paged<{ users?: AdminUser[] }>>(
        `${USERS_BASE}${query({
          status: opts.status,
          q: opts.q,
          page: num(opts.page),
          pageSize: num(opts.pageSize),
          sort: opts.sort,
        })}`,
      ).then((res) => ({
        users: Array.isArray(res?.users) ? res.users : [],
        total: res?.total ?? 0,
        page: res?.page ?? 1,
        pageSize: res?.pageSize ?? 10,
      })),

    create: (body: CreatePayload) => call<WithTempPassword>(USERS_BASE, json(body)),

    update: (id: number, body: UpdatePayload) =>
      call<{ user: AdminUser }>(`${USERS_BASE}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    disable: (id: number, reason: string) =>
      call<{ user: AdminUser }>(`${USERS_BASE}/${id}/disable`, json({ reason })),

    enable: (id: number) => call<{ user: AdminUser }>(`${USERS_BASE}/${id}/enable`, json({})),

    resetPassword: (id: number, password?: string) =>
      call<WithTempPassword>(`${USERS_BASE}/${id}/password`, json(password ? { password } : {})),

    unlock: (id: number) => call<{ user: AdminUser }>(`${USERS_BASE}/${id}/unlock`, json({})),
  }
}

export type AdminsApi = ReturnType<typeof adminsApi>

export function useAdmins(): AdminsApi {
  const { call } = useSession()
  return useMemo(() => adminsApi(call), [call])
}
